import { Config } from "@/shared/config";
import {
  DrivewealthAccountManagementType,
  DrivewealthAccountPerformanceTimePeriod,
  InstrumentPerformanceGraphTimePeriod
} from "@/shared/types";

const drivewealthServiceUri = Config.DrivewealthServicebaseUri;

export default {
  get drivewealthServiceV1 () {
    return `${drivewealthServiceUri}/drivewealth-investment-service/v1`;
  },
  get drivewealthServiceV2 () {
    return `${drivewealthServiceUri}/drivewealth-investment-service/v2`;
  },
  get funds (): string {
    return `${this.drivewealthServiceV1}/funds`;
  },
  get curatedRtqFunds (): string {
    return `${this.drivewealthServiceV1}/funds/get-curated-rtq-funds`;
  },
  get accountSummary (): string {
    return `${this.drivewealthServiceV1}/summary`;
  },
  get accounts (): string {
    return `${this.drivewealthServiceV1}/accounts`;
  },
  updateAccountV2 (accountId: number): string {
    return `${this.drivewealthServiceV2}/accounts/update/${accountId}`;
  },
  closeAccount (accountId: number): string {
    return `${this.drivewealthServiceV2}/accounts/closeAccount/${accountId}`;
  },
  get accountCloseReasons (): string {
    return `${this.drivewealthServiceV2}/accounts/accountCloseReasons`;
  },
  getDocumentsV2 (page: number, pageSize: number, dwAccountId: number): string {
    return `${this.drivewealthServiceV2}/documents/${dwAccountId}?page=${page}&pageSize=${pageSize}`;
  },
  getDocumentUrl (id: number) {
    return `${this.drivewealthServiceV1}/documents/${id}`;
  },
  pendingActivityByAccount (accountId: number): string {
    return `${this.drivewealthServiceV2}/activity-history/${accountId}/pending`;
  },
  getCompletedActivityByAccount (page: number, pageSize: number, accountId: number): string {
    return `${this.drivewealthServiceV2}/activity-history/${accountId}/complete?page=${page}&pageSize=${pageSize}`;
  },
  getManagedTransfersActivityByAccount (accountId: number): string {
    return `${this.drivewealthServiceV2}/activity-history/${accountId}/managed-transfers`;
  },
  postCancelOrderRequest (bendOrderId: string) {
    return `${this.drivewealthServiceV2}/order-request/cancel/${bendOrderId}`;
  },
  postOrderRequestByAccount (accountId: number) {
    return `${this.drivewealthServiceV2}/order-request/${accountId}`;
  },
  servicerPostOrderRequestByAccount (accountId: number, partnerId: number) {
    return `${this.drivewealthServiceV2}/order-request/${partnerId}/servicer/${accountId}`;
  },
  get dayTrades () {
    return `${this.drivewealthServiceV1}/day-trades`;
  },
  getInstrument (stockSymbol: string) {
    return `${this.drivewealthServiceV1}/instruments/${stockSymbol}`;
  },
  getInstrumentByAccountId (stockSymbol: string, accountId: number) {
    return `${this.drivewealthServiceV1}/instruments/${stockSymbol}/${accountId}`;
  },
  getInstrumentFundResearchUrl (stockSymbol: string) {
    return `${this.drivewealthServiceV1}/instruments/fund-research-url/${stockSymbol}`;
  },
  get searchInstruments () {
    return `${this.drivewealthServiceV1}/instrument-search`;
  },
  searchInstrumentsByAccountId (accountId: number, pageNumber: number, pageSize: number, availableToBuyOnly: boolean) {
    return `${this.drivewealthServiceV1}/instrument-search/${accountId}/${pageNumber}/${pageSize}/${availableToBuyOnly}`;
  },
  searchInstrumentsWithFundType () {
    return `${this.drivewealthServiceV1}/instruments/instrument-search`;
  },
  getAssetClasses () {
    return `${this.drivewealthServiceV1}/instruments/asset-classes`;
  },
  getFundCompanies () {
    return `${this.drivewealthServiceV1}/instruments/fund-companies`;
  },
  get sweep () {
    return `${this.drivewealthServiceV1}/sweep`;
  },
  get sweepV2 () {
    return `${this.drivewealthServiceV2}/sweep`;
  },
  sweepAddInstrument (accountId: number, stockSymbol: string) {
    return `${this.drivewealthServiceV2}/sweep/add-instrument/${accountId}/${stockSymbol}`;
  },
  get sweepStatus () {
    return `${this.drivewealthServiceV1}/sweep/status`;
  },
  get sweepStatusPartnerManagement () {
    return `${this.drivewealthServiceV1}/sweep/status-partner-management`;
  },
  get employeeInvestmentType () {
    return `${this.drivewealthServiceV1}/investment-type`;
  },
  partnerInvestmentType (partnerId: number) {
    return `${this.drivewealthServiceV1}/investment-type/partner/${partnerId}`;
  },
  employerInvestmentType (employerId: number) {
    return `${this.drivewealthServiceV1}/investment-type/employer/${employerId}`;
  },
  get riskToleranceQuestionnaire () {
    return `${this.drivewealthServiceV1}/risk-tolerance-questionnaire`;
  },
  get evaluateRiskToleranceQuestionnaire () {
    return `${this.drivewealthServiceV1}/risk-tolerance-questionnaire/evaluate`;
  },
  get riskQuizAgeRevision () {
    return `${this.drivewealthServiceV1}/risk-quiz/age-revision`;
  },
  get riskQuizAgePortfolios () {
    return `${this.drivewealthServiceV1}/risk-quiz/age-portfolio`;
  },
  instrumentLists (listType: string) {
    return `${this.drivewealthServiceV1}/instrument-list/all?listtype=${listType}`;
  },
  instrumentList (instrumentListId: number) {
    return `${this.drivewealthServiceV1}/instrument-list/list/${instrumentListId}`;
  },
  get instrumentMetadata () {
    return `${this.drivewealthServiceV1}/instrument-list/metadata/`;
  },
  instrumentListCsv (instrumentListId: number) {
    return `${this.drivewealthServiceV1}/instrument-list/list/${instrumentListId}/csv`;
  },
  get createInstrumentList () {
    return `${this.drivewealthServiceV1}/instrument-list/create`;
  },
  get enrollment () {
    return `${this.drivewealthServiceV1}/enrollment`;
  },
  get accountClaims () {
    return `${this.drivewealthServiceV2}/accounts/account-claims`;
  },
  claimAccount (accountId: number): string {
    return `${this.drivewealthServiceV2}/accounts/${accountId}/claimAccount`;
  },
  liquidateAccount (dwAccountId: number) {
    return `${this.drivewealthServiceV2}/accounts/${dwAccountId}/liquidate`;
  },
  partnerLiquidateAccount (dwAccountId: number, partnerId: number) {
    return `${this.drivewealthServiceV2}/accounts/${dwAccountId}/partnerliquidate/${partnerId}`;
  },
  get enrollNow () {
    return `${this.drivewealthServiceV1}/enrollment/enroll-now`;
  },
  partnerInvestmentList (partnerId: number) {
    return `${this.drivewealthServiceV1}/instrument-list/partner/${partnerId}`;
  },
  postManagedDepositByAccount (accountId: number) {
    return `${this.drivewealthServiceV2}/managed-transfer/${accountId}/deposit`;
  },
  postManagedRedemptionByAccount (accountId: number) {
    return `${this.drivewealthServiceV2}/managed-transfer/${accountId}/redemption`;
  },
  servicerPostManagedRedemptionByAccount (accountId: number, partnerId: number) {
    return `${this.drivewealthServiceV2}/managed-transfer/${partnerId}/servicer/redemption/${accountId}`;
  },
  get demoMockGetRosters () {
    return `${this.drivewealthServiceV1}/demo-mock/get-rosters`;
  },
  get demoMockGetAreRostersOutOfDate () {
    return `${this.drivewealthServiceV1}/demo-mock/get-are-rosters-out-of-date`;
  },
  get demoMockOpenBrokerageRoster () {
    return `${this.drivewealthServiceV1}/demo-mock/open-brokerage-roster`;
  },
  get demoMockRiaManagedRoster () {
    return `${this.drivewealthServiceV1}/demo-mock/ria-managed-roster`;
  },
  get demoMockClearAllRosters () {
    return `${this.drivewealthServiceV1}/demo-mock/clear-rosters`;
  },
  wlpInstrumentList (custodian: number, investmentOption: DrivewealthAccountManagementType) {
    return `${this.drivewealthServiceV1}/manage-investment-list/${custodian}/${investmentOption}`;
  },
  employerDefaultInstrumentLists (employerId: number) {
    return `${this.drivewealthServiceV1}/manage-investment-list/${employerId}/get-defaults`;
  },
  custodianDefaultInstrumentLists (custodian: number) {
    return `${this.drivewealthServiceV1}/manage-investment-list/get-custodian-defaults/${custodian}`;
  },
  get saveEmployerDefaultList () {
    return `${this.drivewealthServiceV1}/manage-investment-list/set-employer-default-list`;
  },
  get saveCustodianDefaultList () {
    return `${this.drivewealthServiceV1}/manage-investment-list/set-custodian-default-list`;
  },
  get getManagedPortfolio () {
    return `${this.drivewealthServiceV1}/manage-investment-list/managed-portfolio`;
  },
  accountIdsV2 () {
    return `${this.drivewealthServiceV2}/accounts/ids`;
  },
  accountSummaryV2 (dwAccountId: number): string {
    return `${this.drivewealthServiceV2}/summary/${dwAccountId}`;
  },
  get accountSummariesV2 (): string {
    return `${this.drivewealthServiceV2}/summary`;
  },
  get closedAccountSummaries (): string {
    return `${this.drivewealthServiceV2}/summary/closed`;
  },
  employerMigrationPeriod (employerId: number) {
    return `${this.drivewealthServiceV2}/migration-period/${employerId}`;
  },
  getPartnerAccountsMigration (partnerId: number): string {
    return `${this.drivewealthServiceV2}/accounts-migration/${partnerId}`;
  },
  updatePartnerAccountsMigration (partnerId: number): string {
    return `${this.drivewealthServiceV2}/accounts-migration/update/${partnerId}`;
  },
  createPartnerAccountsMigration (partnerId: number): string {
    return `${this.drivewealthServiceV2}/accounts-migration/create/${partnerId}`;
  },
  getAccountMigrationPeriodStatus () {
    return `${this.drivewealthServiceV2}/accounts-migration/status`;
  },
  riskToleranceQuetionnaire (): string {
    return `${this.drivewealthServiceV2}/risk-tolerance-questionnaire`;
  },
  postRiskToleranceQuetionnaire (saveToDb: boolean): string {
    return `${this.drivewealthServiceV2}/risk-tolerance-questionnaire/curated/calculate-score/${saveToDb}`;
  },
  evaluateManagedRtqAnswers (): string {
    return `${this.drivewealthServiceV2}/risk-tolerance-questionnaire/managed`;
  },
  userRtqEligibility (): string {
    return `${this.drivewealthServiceV2}/risk-tolerance-questionnaire/rtq-eligibility`;
  },
  dividendReinvestmentDisclosure (): string {
    return `${this.drivewealthServiceV1}/disclosures/dividend-reinvestment`;
  },
  getCustodianDefaultAgreement ( custodian: number): string {
    return `${this.drivewealthServiceV2}/custodian-default-agreement/custodian-agreements/${custodian}`;
  },
  saveCustodianDefaultAgreement (): string {
    return `${this.drivewealthServiceV2}/custodian-default-agreement/update-default-agreement`;
  },
  getEmployerAgreement ( custodian: number): string {
    return `${this.drivewealthServiceV2}/employer-agreement/employer-agreements/${custodian}`;
  },
  saveEmployerAgreement (): string {
    return `${this.drivewealthServiceV2}/employer-agreement/update-agreement`;
  },
  getUserAgreementDocumentKey (accountType: string): string {
    return `${this.drivewealthServiceV2}/documents/get-user-agreement-document-key/${accountType}`;
  },
  investmentAccountSearch (cognitoName: string): string {
    return `${this.drivewealthServiceV2}/accounts/investment-account-search/${cognitoName}`;
  },
  get uploadAccountCloseFile () {
    return `${this.drivewealthServiceV2}/accounts/investment-account-close-upload`;
  },
  uploadBulkLiquidateFile (partnerId: number) {
    return `${this.drivewealthServiceV2}/accounts/${partnerId}/bulk-liquidate`;
  },
  accountCloseFileRequests (page: number, pageSize: number) {
    return `${this.drivewealthServiceV2}/accounts/investment-account-close-requests/${page}/${pageSize}`;
  },
  downloadResultsFileCsv (drivewealthAccountCancelUploadId: number) {
    return `${this.drivewealthServiceV2}/accounts/investment-account-close-result/${drivewealthAccountCancelUploadId}`;
  },
  bulkLiquidationFileRequests (partnerId: number, page: number, pageSize: number) {
    return `${this.drivewealthServiceV2}/accounts/${partnerId}/bulk-liquidate-requests/${page}/${pageSize}`;
  },
  bulkLiquidationStatus (partnerId: number, fileNameGuid: string) {
      return `${this.drivewealthServiceV2}/accounts/${partnerId}/bulk-liquidate-status/${fileNameGuid}`;
  },
  getAccountPerformanceData (accountId: number, timePeriod: DrivewealthAccountPerformanceTimePeriod) {
    return `${this.drivewealthServiceV2}/account-performance/${accountId}?timePeriod=${timePeriod}`;
  },
  getInvestmentBalanceGraphData (accountId: number, timePeriod: DrivewealthAccountPerformanceTimePeriod) {
    return `${this.drivewealthServiceV2}/investments/${accountId}/balances/${timePeriod}`;
  },
  getAccountPerformanceRateOfReturn (accountId: number) {
    return `${this.drivewealthServiceV2}/account-performance/rate-of-return/${accountId}`;
  },
  getInstrumentPerformanceData (stockSymbol: string) {
    return `${this.drivewealthServiceV1}/instruments/performance/${stockSymbol}`;
  },
  getInstrumentPerformanceGraphData (stockSymbol: string, dateStart: string, dateEnd: string) {
    return `${this.drivewealthServiceV1}/instruments/history/${stockSymbol}?dateStart=${dateStart}&dateEnd=${dateEnd}`;
  },
  getBalanceThresholds () {
    return `${this.drivewealthServiceV2}/balance-thresholds`;
  },
  getInvestmentDailyBalances (startDate: string, endDate: string, accountId: number) {
    // tslint:disable-next-line:max-line-length
    return `${this.drivewealthServiceV2}/investments/list?startDate=${startDate}&endDate=${endDate}&accountId=${accountId}`;
  },
  get hsaAccountStatuses () {
    return `${this.drivewealthServiceV2}/accounts/legacy-statuses`;
  },
};
